import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { NewsDto, NewsService } from '@millionshow/api-client';
import { Icon } from '@millionshow/client-libs';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import PaginationComponent from '../../components/pagination/pagination.component';
import { NotFoundComponent } from '../../components/not-found/not-found.component';
import { SeoService } from '../../services/seo.service';

@Component({
  selector: 'app-donation-page',
  imports: [
    CommonModule,
    Icon,
    RouterLink,
    PaginationComponent,
    NotFoundComponent,
  ],
  templateUrl: './donation.page.html',
})
export default class DonationPage {
  private readonly newsService = inject<NewsService>(NewsService);
  private readonly seoService = inject(SeoService);

  query = signal<string>('');
  pageNo = signal<number>(1);
  pageSize = signal<number>(12);

  private searchSubject = new Subject<string>();

  constructor() {
    this.seoService.set({
      title: '기부사랑',
    });

    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(searchQuery => {
        this.query.set(searchQuery);
        this.pageNo.set(1);
      });
  }

  $newsData = rxResource({
    stream: ({ params }) => {
      return this.newsService.newsControllerSearchV1({
        pageNo: params.pageNo,
        pageSize: params.pageSize,
        isExposed: true,
        query: params.query,
        type: params.type,
      });
    },
    params: () => ({
      pageNo: this.pageNo(),
      pageSize: this.pageSize(),
      query: this.query(),
      type: 'GOODSPACE' as NewsDto['type'],
    }),
  });

  newsList = computed(() => this.$newsData.value()?.items || []);

  totalCount = computed(
    () => this.$newsData.value()?.pageInfo?.totalItems || 0
  );

  totalPages = computed<number[]>(() => {
    const totalItems = this.$newsData.value()?.pageInfo?.totalItems || 0;
    const count = Math.max(1, Math.ceil(totalItems / this.pageSize()));
    return Array.from({ length: count }, (_, i) => i + 1);
  });

  currentPage = computed(() => this.pageNo());

  onSearch(event: Event) {
    const input = event.target as HTMLInputElement;
    this.searchSubject.next(input.value);
  }

  onPageChange(page: number): void {
    this.pageNo.set(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  stripHtml(html: string | null | undefined): string {
    if (!html) return '';
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }

  extractFirstImage(html: string | null | undefined): string | null {
    if (!html) return null;
    const match = html.match(/<img[^>]+src=["']([^"']+)["']/);
    return match ? match[1] : null;
  }
}
